import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Settings, Check, X as XIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useCookies, CookiePreferences } from "@/hooks/useCookies";

const CookieBanner = () => {
  const { cookieConsent, isLoaded, saveCookiePreferences } = useCookies();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Siempre true, no se puede desactivar
    analytics: false,
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    // Mostrar banner solo si no hay consentimiento guardado y el hook está cargado
    if (isLoaded && !cookieConsent) {
      setShowBanner(true);
    }
  }, [isLoaded, cookieConsent]);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    saveCookiePreferences(allAccepted);
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
    };
    setPreferences(necessaryOnly);
    saveCookiePreferences(necessaryOnly);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'necessary') return; // No se puede desactivar
    setPreferences(prev => ({
      ...prev,
      [type]: value,
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Banner principal */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 p-4">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-taxby-violet mb-2">
                  Utilizamos cookies para mejorar tu experiencia
                </h3>
                <p className="text-gray-600 text-sm">
                  Utilizamos cookies propias y de terceros para analizar el tráfico web, 
                  personalizar contenido y publicidad, y proporcionar funcionalidades de redes sociales. 
                  Puedes gestionar tus preferencias en cualquier momento.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Configurar
                </Button>
                <Button
                  onClick={handleAcceptNecessary}
                  variant="outline"
                  size="sm"
                >
                  Solo necesarias
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  size="sm"
                  className="bg-taxby-purple hover:bg-taxby-purple/90"
                >
                  Aceptar todas
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel de configuración */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-taxby-violet">
                  Configuración de Cookies
                </h2>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Cookies Necesarias */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies Necesarias</h3>
                      <p className="text-sm text-gray-600">Siempre activas</p>
                    </div>
                    <div className="flex items-center">
                      <Check className="h-5 w-5 text-taxby-purple" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar. 
                    Incluyen cookies de sesión, seguridad y preferencias básicas.
                  </p>
                </div>

                {/* Cookies Analíticas */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies Analíticas</h3>
                      <p className="text-sm text-gray-600">Nos ayudan a mejorar el sitio</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-taxby-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-taxby-purple"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Nos permiten analizar el uso del sitio web para mejorar su funcionamiento y experiencia de usuario.
                  </p>
                </div>

                {/* Cookies Funcionales */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies Funcionales</h3>
                      <p className="text-sm text-gray-600">Mejoran la funcionalidad</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-taxby-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-taxby-purple"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Permiten que el sitio web recuerde tus preferencias y proporcionen funcionalidades mejoradas.
                  </p>
                </div>

                {/* Cookies de Marketing */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-taxby-violet">Cookies de Marketing</h3>
                      <p className="text-sm text-gray-600">Publicidad personalizada</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-taxby-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-taxby-purple"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Se utilizan para mostrar publicidad relevante y medir la efectividad de nuestras campañas.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSavePreferences}
                    className="bg-taxby-purple hover:bg-taxby-purple/90"
                  >
                    Guardar preferencias
                  </Button>
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Puedes cambiar tus preferencias en cualquier momento visitando nuestra{' '}
                  <Link to="/politica-cookies" className="text-taxby-purple hover:underline">
                    Política de Cookies
                  </Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner; 